import { getEnvironmentFields } from "../../../actions/envAnalytics";
import { getProductFields } from "../../../actions/reports";

import { TEMPLATE_SIDES } from "./TemplateContext";

const TEMPLATE_TAGS_COLOR = {
  red: {
    backgroundColor: "#E63559",
    borderColor: "#F28DA1",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
  blue: {
    backgroundColor: "#3874FF",
    borderColor: "#26ABE1",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
  yellow: {
    backgroundColor: "#E1B226",
    borderColor: "rgb(244 222 155)",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
  orange: {
    backgroundColor: "#F98006",
    borderColor: "rgb(251 196 140)",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
  green: {
    backgroundColor: "#1EC04A",
    borderColor: "#52E378",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
  analyticsblue: {
    backgroundColor: "#1863DC",
    borderColor: "#26ABE1",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
  teal: {
    backgroundColor: "#0C6B83",
    borderColor: "#26ABE1",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
  purple: {
    backgroundColor: "#8A1CFF",
    borderColor: "#6649ff",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
  sage: {
    backgroundColor: "#5D7D23",
    borderColor: "#5d7025",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
  readOnlyDefault: {
    background: "linear-gradient(180deg, #002925 0%, #2E746C 100%)",
    borderColor: "#E0E6E3",
    boxShadow: "#e9e8e8 1px 2px 3px 3px",
  },
};
export default TEMPLATE_TAGS_COLOR;

export const READ_ONLY_TEMPLATE_STYLE = {
  background: "linear-gradient(180deg, #002925 0%, #2E746C 100%)",
  border: "1px solid #E0E6E3",
};

/** Accent bar / card fill for template list items — read-only defaults use gradient. */
export const getTemplateAccentStyle = (template) => {
  if (template?.read_only === true) {
    return { background: READ_ONLY_TEMPLATE_STYLE.background };
  }
  const tag = template?.tags?.[0];
  const tagStyle = TEMPLATE_TAGS_COLOR[tag] || TEMPLATE_TAGS_COLOR.blue;
  return { backgroundColor: tagStyle.backgroundColor };
};

/** Tag pill style for customized COA list rows. */
export const getCoaListItemTemplateTagStyle = (elem, readOnlyTemplateIds = new Set()) => {
  const isReadOnly = elem.read_only === true
    || (elem.template_id && readOnlyTemplateIds.has(String(elem.template_id)));
  if (isReadOnly) {
    return getTemplateAccentStyle({ read_only: true });
  }
  const tag = elem.tags?.[0];
  if (!tag) {
    return { backgroundColor: "#A9A9A9" };
  }
  const tagStyle = TEMPLATE_TAGS_COLOR[tag] || TEMPLATE_TAGS_COLOR.blue;
  return { backgroundColor: tagStyle.backgroundColor };
};

/**
 * Fetch report fields for template module — productfields or environmentfields by side.
 * Normalizes environment link_pattern_fields to link_report shape used by reports table.
 */
export const getTemplateReportFields = async (side, skipMaintenanceRedirect = false) => {
  if (side === TEMPLATE_SIDES.ENVIRONMENTAL) {
    const result = await getEnvironmentFields(skipMaintenanceRedirect);
    if (!result?.success) {
      return result;
    }
    const {
      fields, link_pattern_fields: linkPatternFields, delimiter, delimeter,
    } = result;
    const fieldDelimiter = delimiter || delimeter || ".";
    const regex = new RegExp(fieldDelimiter, "g");
    const linkReport = (linkPatternFields || [])
      .filter(Boolean)
      .flatMap((field) => field.split(regex));
    const fieldsMap = {};
    (fields || []).forEach((fieldObj) => {
      fieldsMap[fieldObj.json_field] = fieldObj;
    });
    return {
      success: true,
      fields,
      fieldsMap,
      link_report: linkReport,
      sampleid_fields: result.sampleid_fields,
    };
  }
  return getProductFields(skipMaintenanceRedirect, { side });
};

/**
 * Get linked field vals as an array and as a string delimited by a dot
 * @param {Object} valuesObj linked field: value obj i.e. {item: "test", best_by: ""}
 * @param {Array} linkReportFields linked field json fields i.e. [item, best_by]
 * @returns {Object} linked field vals as an array and as a string (delimited by a dot)
 */
const getProductIdentifier = (valuesObj, linkReportFields) => {
  const identifierArr = [];
  linkReportFields.forEach((json_field) => {
    const value = valuesObj[json_field];
    if (value) {
      identifierArr.push(value);
    }
  });
  return { identifierArr, identifierStr: identifierArr.join(" \u2022 ") };
};

/**
 * Get list of distinct identfiers for samples in sampleList
 * @param {Array} sampleList
 * @param {Array} linkReportKeys linked field json fields i.e. [item, best_by]
 */
export const getDistinctIdentifiersForSampleList = (sampleList, linkReportKeys) => {
  const identifiersList = [];
  const productsSeenSoFar = new Set();
  sampleList.forEach(
    (sample) => {
      const { identifierArr, identifierStr } = getProductIdentifier(sample, linkReportKeys);
      if (!productsSeenSoFar.has(identifierStr)) {
        productsSeenSoFar.add(identifierStr);
        identifiersList.push(identifierArr);
      }
    },
  );
  return identifiersList;
};

export function createAndAppendIdentifierList(response, linkReportKeys) {
  const result = response.result;
  if (linkReportKeys && linkReportKeys.length) {
    result.forEach(
      (report) => {
        report.all_identifiers = getDistinctIdentifiersForSampleList(report.sample_list, linkReportKeys);
      },
    );
  }
  return result;
}

/** OS-reserved filename characters — invalid in COA names. */
export const COA_NAME_INVALID_CHARS_REGEX = /[\\/:*?"<>|]/;

export const validateCoaName = (name) => {
  const trimmed = (name || "").trim();
  if (!trimmed) {
    return { valid: false, message: "COA name cannot be empty." };
  }
  if (COA_NAME_INVALID_CHARS_REGEX.test(trimmed)) {
    return {
      valid: false,
      message: "COA name cannot contain reserved characters: \\ / : * ? \" < > |",
    };
  }
  return { valid: true, message: "" };
};

/** producttemplates API duplicate-name responses (e.g. "Name should be unique for template"). */
export const isDuplicateTemplateNameMessage = (message) => (
  (message || "").toLowerCase().includes("name should be unique")
);
